import { CALL_STATUS, type PiiTextAlignmentSummary } from "@repo/common";
import {
	approveCallPiiReview,
	db,
	deleteCallCascade,
	functions,
	getAdjacentCallId,
	getCallPiiSidecar,
	isEmptyCall,
	type OutboundCallContext,
	type OutboundPlaybook,
	type OutboundTask,
	type OutboundTaskAttempt,
	payloadEntries,
	purgeCallPiiSidecar,
	purgeCallTwilioRecording,
	resolveCallParticipantDisplay,
	resolveCallPiiReprocess,
	resolveOutboundCallContext,
	runManualEvaluation,
	startCallPiiRedaction,
	startCallPiiReprocess,
	storage,
	useCallNavigationShortcuts,
} from "@repo/ui";
import { collectionGroup, doc, getDoc, onSnapshot } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { getDownloadURL, ref } from "firebase/storage";
import {
	AlertCircle,
	ArrowRight,
	CheckSquare,
	ChevronLeft,
	ChevronRight,
	Eye,
	FileText,
	FlaskConical,
	Info,
	Loader2,
	MicOff,
	Phone,
	Play,
	Search,
	ShieldAlert,
	ShieldCheck,
	Sparkles,
	Trash2,
	User,
} from "lucide-react";
import {
	Fragment,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { toast } from "sonner";
import { useData } from "../contexts/DataContext";
import type { Call, EvaluationReport } from "../data/calls";
import {
	getPiiRedactionPresentation,
	getPiiSidecarPresentation,
	type PiiRedactionPresentation,
} from "../utils/piiPresentation";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "./ui/alert-dialog";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "./ui/card";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "./ui/dialog";
import { Input } from "./ui/input";
import {
	Pagination,
	PaginationContent,
	PaginationEllipsis,
	PaginationItem,
	PaginationLink,
	PaginationNext,
	PaginationPrevious,
} from "./ui/pagination";
import { ScrollArea } from "./ui/scroll-area";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "./ui/select";
import { Separator } from "./ui/separator";
import { Switch } from "./ui/switch";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "./ui/table";
import { Textarea } from "./ui/textarea";

function getCallFallbackState(call: Pick<Call, "status" | "transcript">) {
	const hasNoTranscript = !call.transcript?.trim();
	const isProcessing =
		hasNoTranscript &&
		(call.status === CALL_STATUS.PROCESSING ||
			call.status === CALL_STATUS.IN_PROGRESS);
	const isFailed = hasNoTranscript && call.status === CALL_STATUS.FAILED;
	const isNoAudio =
		hasNoTranscript &&
		(call.status === CALL_STATUS.COMPLETED ||
			call.status === CALL_STATUS.ABANDONED);

	if (isProcessing) {
		return {
			kind: "processing" as const,
			title: "Processing call",
			summary:
				"Call processing is still in progress. Transcript and summary will appear when processing finishes.",
		};
	}
	if (isFailed) {
		return {
			kind: "failed" as const,
			title: "Processing failed",
			summary: "Call processing failed before a transcript was created.",
		};
	}
	if (isNoAudio) {
		return {
			kind: "no-audio" as const,
			title: "No Audio / Silence",
			summary: "The call contained no recognizable audio or speech.",
		};
	}
	return null;
}

function PiiRedactionBadge({
	presentation,
	compact = false,
}: {
	presentation: PiiRedactionPresentation;
	compact?: boolean;
}) {
	return (
		<Badge
			variant="outline"
			className={`gap-1.5 whitespace-nowrap font-semibold ${
				compact ? "px-1.5 py-0.5 text-[10px]" : "px-2.5 py-1 text-xs"
			} ${
				presentation.kind === "complete"
					? "bg-emerald-50 text-emerald-700 border-emerald-200"
					: presentation.kind === "partial"
						? "bg-amber-50 text-amber-800 border-amber-200"
						: presentation.kind === "review"
							? "bg-amber-100 text-amber-900 border-amber-300"
							: presentation.kind === "error"
								? "bg-red-50 text-red-700 border-red-200"
								: "bg-slate-100 text-slate-600 border-slate-200"
			}`}
		>
			{presentation.kind === "complete" ? (
				<ShieldCheck className="w-3.5 h-3.5" />
			) : presentation.kind === "review" ? (
				<ShieldAlert className="w-3.5 h-3.5" />
			) : presentation.kind === "partial" ? (
				<Loader2 className="w-3.5 h-3.5" />
			) : (
				<ShieldAlert className="w-3.5 h-3.5" />
			)}
			{presentation.label}
		</Badge>
	);
}

function PiiAlignmentMethodBadges({
	alignment,
	compact = false,
}: {
	alignment?: PiiTextAlignmentSummary | null;
	compact?: boolean;
}) {
	if (!alignment || alignment.methodsUsed.length === 0) return null;
	return (
		<div className="flex flex-wrap items-center gap-1">
			{alignment.methodsUsed.map((method) => (
				<Badge
					key={method}
					variant="outline"
					className={compact ? "h-4 px-1 text-[8px]" : "h-5 px-1.5 text-[10px]"}
				>
					{method === "llm"
						? "LLM"
						: `${method[0].toUpperCase()}${method.slice(1)}`}
				</Badge>
			))}
			{typeof alignment.finalConfidence === "number" && (
				<span
					className={
						compact ? "text-[8px] text-slate-500" : "text-[10px] text-slate-500"
					}
				>
					{Math.round(alignment.finalConfidence * 100)}% final
				</span>
			)}
		</div>
	);
}

function getErrorMessage(error: unknown, fallback: string): string {
	return error instanceof Error && error.message ? error.message : fallback;
}

type PiiReprocessCandidate = {
	id: string;
	status: string;
	failureReason?: string;
	original?: { formattedTranscript?: string };
	preview?: {
		formattedTranscript?: string;
		alignment?: {
			resolvedOccurrences?: number;
			expectedOccurrences?: number;
		};
		entities?: Array<{ type: string }>;
	};
	artifacts?: { finalAudioGcsUri?: string };
};

export function CallsPage({
	selectedCallSid,
	onSelectedCallSidChange,
	onOpenPiiCase,
}: {
	selectedCallSid?: string | null;
	onSelectedCallSidChange?: (callSid: string | null) => void;
	onOpenPiiCase?: (companyId: string, callSid: string) => void;
}) {
	const { calls, companies } = useData();
	const [searchQuery, setSearchQuery] = useState("");
	const [currentPage, setCurrentPage] = useState(1);
	const [selectedCompanyId, setSelectedCompanyId] = useState<string>("all");
	const [directionFilter, setDirectionFilter] = useState<
		"all" | "inbound" | "outbound"
	>("all");
	const [playbookFilter, setPlaybookFilter] = useState("all");
	const [hideEmptyCalls, setHideEmptyCalls] = useState(true);
	const [selectedCall, setSelectedCall] = useState<Call | null>(null);
	const [isDetailsDialogOpen, setIsDetailsDialogOpen] = useState(false);
	const [outboundPlaybooks, setOutboundPlaybooks] = useState<
		OutboundPlaybook[]
	>([]);
	const [outboundTasks, setOutboundTasks] = useState<OutboundTask[]>([]);
	const [outboundAttempts, setOutboundAttempts] = useState<
		OutboundTaskAttempt[]
	>([]);

	// Derived selected call to ensure we have the latest data from the collection
	const activeCall = useMemo(() => {
		if (!selectedCall) return null;
		return calls.find((c) => c.id === selectedCall.id) || selectedCall;
	}, [selectedCall, calls]);
	const activeCallFallback = activeCall
		? getCallFallbackState(activeCall)
		: null;
	const activePiiPresentation = activeCall
		? getPiiRedactionPresentation(activeCall)
		: null;
	const activePiiSidecarPresentation = activeCall
		? getPiiSidecarPresentation(activeCall)
		: null;

	// Audio Player State
	const [playingCallId, setPlayingCallId] = useState<string | null>(null);
	const [audioUrl, setAudioUrl] = useState<string | null>(null);
	const [playbackRate, setPlaybackRate] = useState(1);
	const [isEvaluating, setIsEvaluating] = useState(false);
	const [redactingCallIds, setRedactingCallIds] = useState<Set<string>>(
		() => new Set(),
	);
	const [reviewCall, setReviewCall] = useState<Call | null>(null);
	const [correctionCall, setCorrectionCall] = useState<Call | null>(null);
	const [correctionReason, setCorrectionReason] = useState("");
	const [openingCorrection, setOpeningCorrection] = useState(false);
	const [reviewReason, setReviewReason] = useState("");
	const [isApprovingPiiReview, setIsApprovingPiiReview] = useState(false);
	const [reprocessCall, setReprocessCall] = useState<Call | null>(null);
	const [reprocessCandidateId, setReprocessCandidateId] = useState<
		string | null
	>(null);
	const [reprocessCandidate, setReprocessCandidate] =
		useState<PiiReprocessCandidate | null>(null);
	const [isStartingReprocess, setIsStartingReprocess] = useState(false);
	const [isResolvingReprocess, setIsResolvingReprocess] = useState(false);
	const [reprocessAudioUrl, setReprocessAudioUrl] = useState<string | null>(
		null,
	);
	const [purgingTwilioCallIds, setPurgingTwilioCallIds] = useState<Set<string>>(
		() => new Set(),
	);
	const [purgingPiiSidecarCallIds, setPurgingPiiSidecarCallIds] = useState<
		Set<string>
	>(() => new Set());
	const [isLoadingPiiSidecar, setIsLoadingPiiSidecar] = useState(false);
	const [piiSidecar, setPiiSidecar] = useState<{
		path: string;
		data: unknown;
	} | null>(null);
	const [callPendingDeletion, setCallPendingDeletion] = useState<Call | null>(
		null,
	);
	const [isDeletingCall, setIsDeletingCall] = useState(false);
	const [evaluationReport, setEvaluationReport] =
		useState<EvaluationReport | null>(null);
	const [isLoadingEvaluation, setIsLoadingEvaluation] = useState(false);
	const audioRef = useRef<HTMLAudioElement>(null);

	const itemsPerPage = 10;

	useEffect(() => {
		const unsubscribePlaybooks = onSnapshot(
			collectionGroup(db, "outboundPlaybooks"),
			(snapshot) =>
				setOutboundPlaybooks(
					snapshot.docs.map((doc) => ({
						...doc.data(),
						id: doc.id,
						path: doc.ref.path,
						companyId: doc.ref.parent.parent?.id || doc.data().companyId || "",
					})) as OutboundPlaybook[],
				),
			(error) => console.error("Failed to load outbound playbooks", error),
		);
		const unsubscribeTasks = onSnapshot(
			collectionGroup(db, "outboundTasks"),
			(snapshot) =>
				setOutboundTasks(
					snapshot.docs.map((doc) => ({
						...doc.data(),
						id: doc.id,
						path: doc.ref.path,
						companyId: doc.ref.parent.parent?.id || doc.data().companyId || "",
					})) as OutboundTask[],
				),
			(error) => console.error("Failed to load outbound tasks", error),
		);
		const unsubscribeAttempts = onSnapshot(
			collectionGroup(db, "outboundTaskAttempts"),
			(snapshot) =>
				setOutboundAttempts(
					snapshot.docs.map((doc) => ({
						...doc.data(),
						id: doc.id,
						path: doc.ref.path,
						companyId: doc.ref.parent.parent?.id || doc.data().companyId || "",
					})) as OutboundTaskAttempt[],
				),
			(error) => console.error("Failed to load outbound task attempts", error),
		);

		return () => {
			unsubscribePlaybooks();
			unsubscribeTasks();
			unsubscribeAttempts();
		};
	}, []);

	const outboundContextByCallId = useMemo(() => {
		const contexts = new Map<string, OutboundCallContext>();
		for (const call of calls) {
			const context = resolveOutboundCallContext({
				call,
				playbooks: outboundPlaybooks.filter(
					(playbook) => playbook.companyId === call.companyId,
				),
				tasks: outboundTasks.filter(
					(task) => task.companyId === call.companyId,
				),
				attempts: outboundAttempts.filter(
					(attempt) => attempt.companyId === call.companyId,
				),
			});
			if (context.isOutbound) contexts.set(call.id, context);
		}
		return contexts;
	}, [calls, outboundPlaybooks, outboundTasks, outboundAttempts]);

	const playbookFilterOptions = useMemo(() => {
		const options = new Map<string, string>();
		for (const playbook of outboundPlaybooks) {
			if (
				playbook.id &&
				(selectedCompanyId === "all" ||
					playbook.companyId === selectedCompanyId)
			) {
				options.set(playbook.id, playbook.name || playbook.id);
			}
		}
		for (const [callId, context] of outboundContextByCallId) {
			const call = calls.find((entry) => entry.id === callId);
			if (
				context.playbookId &&
				(selectedCompanyId === "all" || call?.companyId === selectedCompanyId)
			) {
				options.set(
					context.playbookId,
					context.playbookName || context.playbookId,
				);
			}
		}
		return Array.from(options, ([id, name]) => ({ id, name })).sort((a, b) =>
			a.name.localeCompare(b.name),
		);
	}, [outboundPlaybooks, outboundContextByCallId, selectedCompanyId, calls]);
	const activeOutboundContext = activeCall
		? outboundContextByCallId.get(activeCall.id)
		: undefined;
	const activeOutboundInputEntries = payloadEntries(
		activeOutboundContext?.taskInput,
	);
	const activeOutboundResultEntries = payloadEntries(
		activeOutboundContext?.structuredResult,
	);

	// Fetch evaluation report when a call is selected
	useEffect(() => {
		const fetchEvaluation = async () => {
			if (!activeCall || activeCall.evaluationStatus !== "completed") {
				setEvaluationReport(null);
				return;
			}

			setIsLoadingEvaluation(true);
			try {
				const evalDocRef = doc(
					db,
					`companies/${activeCall.companyId}/calls/${activeCall.id}/evaluations/initial`,
				);
				const evalDoc = await getDoc(evalDocRef);

				if (evalDoc.exists()) {
					setEvaluationReport(evalDoc.data() as EvaluationReport);
				} else {
					console.warn(`Evaluation doc not found for call ${activeCall.id}`);
					setEvaluationReport(null);
				}
			} catch (error) {
				console.error("Error fetching evaluation report:", error);
				setEvaluationReport(null);
			} finally {
				setIsLoadingEvaluation(false);
			}
		};

		if (isDetailsDialogOpen) {
			fetchEvaluation();
		}
	}, [activeCall, isDetailsDialogOpen]);

	// Helper to get company name
	const getCompanyName = (companyId: string) => {
		const company = companies.find((c) => c.id === companyId);
		return company?.name || companyId;
	};

	// Filter and Sort Calls
	const filteredCalls = useMemo(() => {
		let filtered = calls;

		if (hideEmptyCalls) {
			filtered = filtered.filter((call) => !isEmptyCall(call));
		}

		if (selectedCompanyId !== "all") {
			filtered = filtered.filter(
				(call) => call.companyId === selectedCompanyId,
			);
		}

		filtered = filtered.filter((call) => {
			const outboundContext = outboundContextByCallId.get(call.id);
			const isOutbound = Boolean(outboundContext?.isOutbound);
			if (directionFilter === "inbound") return !isOutbound;
			if (directionFilter === "outbound") return isOutbound;
			return true;
		});

		if (playbookFilter !== "all") {
			filtered = filtered.filter(
				(call) =>
					outboundContextByCallId.get(call.id)?.playbookId === playbookFilter,
			);
		}

		if (searchQuery) {
			const lowerQuery = searchQuery.toLowerCase();
			filtered = filtered.filter(
				(call) =>
					call.id.toLowerCase().includes(lowerQuery) ||
					call.caller.name.toLowerCase().includes(lowerQuery) ||
					call.caller.phone.includes(lowerQuery) ||
					call.agent.toLowerCase().includes(lowerQuery) ||
					(call.title && call.title.toLowerCase().includes(lowerQuery)) ||
					(outboundContextByCallId
						.get(call.id)
						?.playbookName?.toLowerCase()
						.includes(lowerQuery) ??
						false),
			);
		}

		return filtered; // already sorted by desc in fetching
	}, [
		calls,
		selectedCompanyId,
		searchQuery,
		outboundContextByCallId,
		directionFilter,
		playbookFilter,
		hideEmptyCalls,
	]);

	// Pagination logic
	const totalPages = Math.ceil(filteredCalls.length / itemsPerPage);
	const startIndex = (currentPage - 1) * itemsPerPage;
	const endIndex = startIndex + itemsPerPage;
	const paginatedCalls = filteredCalls.slice(startIndex, endIndex);
	const filteredCallIds = useMemo(
		() => filteredCalls.map((call) => call.id),
		[filteredCalls],
	);
	const previousCallId = activeCall
		? getAdjacentCallId(filteredCallIds, activeCall.id, "previous")
		: null;
	const nextCallId = activeCall
		? getAdjacentCallId(filteredCallIds, activeCall.id, "next")
		: null;

	// Reset to page 1 when filters change
	// biome-ignore lint/correctness/useExhaustiveDependencies: Filter changes intentionally reset pagination even though the reset does not read their values.
	useEffect(() => {
		setCurrentPage(1);
	}, [
		searchQuery,
		selectedCompanyId,
		directionFilter,
		playbookFilter,
		hideEmptyCalls,
	]);

	const handlePlayAudio = async (call: Call, e?: React.MouseEvent) => {
		if (e) e.stopPropagation();

		if (!call.gcsUri) {
			return;
		}

		setPlayingCallId(call.id);
		setPlaybackRate(1);

		try {
			let url = call.gcsUri;
			if (url.startsWith("gs://")) {
				// Convert gs:// to direct download URL using Firebase Storage
				const storageRef = ref(storage, url);
				url = await getDownloadURL(storageRef);
			}
			setAudioUrl(url);
		} catch (error) {
			console.error(`Error getting audio URL for ${call.gcsUri}:`, error);
			// Sentry.captureException(error);
			alert("Failed to load recording.");
			setPlayingCallId(null);
			setAudioUrl(null);
		}
	};

	const handleViewDetails = (call: Call) => {
		setSelectedCall(call);
		setIsDetailsDialogOpen(true);
		if (onSelectedCallSidChange) {
			onSelectedCallSidChange(call.id);
		} else {
			window.history.pushState(
				null,
				"",
				`/calls/${encodeURIComponent(call.id)}`,
			);
		}
	};

	useEffect(() => {
		if (!selectedCallSid) return;
		const call = calls.find((entry) => entry.id === selectedCallSid);
		if (!call) return;
		setSelectedCall(call);
		setIsDetailsDialogOpen(true);
	}, [calls, selectedCallSid]);

	const handleRunEvaluation = async (call: Call, force = false) => {
		setIsEvaluating(true);
		try {
			await runManualEvaluation({
				companyId: call.companyId,
				callSid: call.id,
				force,
			});
			toast.success(
				force
					? "Re-evaluation started successfully"
					: "Evaluation started successfully",
			);
		} catch (error) {
			console.error("Error running evaluation:", error);
			toast.error("Failed to run evaluation");
		} finally {
			setIsEvaluating(false);
		}
	};

	const handleRedactPii = async (call: Call, e?: React.MouseEvent) => {
		if (e) e.stopPropagation();
		setRedactingCallIds((current) => new Set(current).add(call.id));
		try {
			await startCallPiiRedaction({
				companyId: call.companyId,
				callSid: call.id,
			});
			toast.success("PII redaction started");
		} catch (error: unknown) {
			console.error("Error triggering PII redaction:", error);
			toast.error(getErrorMessage(error, "Failed to trigger PII redaction"));
		} finally {
			setRedactingCallIds((current) => {
				const next = new Set(current);
				next.delete(call.id);
				return next;
			});
		}
	};

	const openPiiReview = (call: Call, e?: React.MouseEvent) => {
		e?.stopPropagation();
		setReviewCall(call);
		setReviewReason("");
	};
	const handleOpenCorrection = async () => {
		if (!correctionCall || !correctionReason.trim()) return;
		setOpeningCorrection(true);
		try {
			const open = httpsCallable(functions, "openCallPiiCorrection");
			await open({
				companyId: correctionCall.companyId,
				callSid: correctionCall.id,
				reason: correctionReason.trim(),
			});
			const { companyId, id } = correctionCall;
			setCorrectionCall(null);
			setCorrectionReason("");
			setIsDetailsDialogOpen(false);
			onOpenPiiCase?.(companyId, id);
		} catch (error) {
			toast.error(getErrorMessage(error, "Could not open PII correction"));
		} finally {
			setOpeningCorrection(false);
		}
	};

	const handleApprovePiiReview = async () => {
		if (!reviewCall || !reviewReason.trim()) return;
		setIsApprovingPiiReview(true);
		try {
			await approveCallPiiReview({
				companyId: reviewCall.companyId,
				callSid: reviewCall.id,
				reason: reviewReason.trim(),
			});
			toast.success("PII review approved and cleanup completed");
			setReviewCall(null);
			setReviewReason("");
		} catch (error: unknown) {
			console.error("Error approving PII review:", error);
			toast.error(getErrorMessage(error, "Failed to approve PII review"));
		} finally {
			setIsApprovingPiiReview(false);
		}
	};

	useEffect(() => {
		if (!reprocessCall || !reprocessCandidateId) return;
		return onSnapshot(
			doc(
				db,
				"companies",
				reprocessCall.companyId,
				"calls",
				reprocessCall.id,
				"piiReprocessCandidates",
				reprocessCandidateId,
			),
			(snapshot) =>
				setReprocessCandidate(
					snapshot.exists()
						? ({ id: snapshot.id, ...snapshot.data() } as PiiReprocessCandidate)
						: null,
				),
			(error) => {
				console.error("Failed to watch PII reprocess candidate:", error);
				toast.error("Failed to load the PII candidate");
			},
		);
	}, [reprocessCall, reprocessCandidateId]);

	useEffect(() => {
		const gcsUri = reprocessCandidate?.artifacts?.finalAudioGcsUri;
		if (!gcsUri) {
			setReprocessAudioUrl(null);
			return;
		}
		let cancelled = false;
		void getDownloadURL(ref(storage, gcsUri))
			.then((url) => {
				if (!cancelled) setReprocessAudioUrl(url);
			})
			.catch((error) => {
				console.error("Failed to load candidate audio:", error);
				if (!cancelled) setReprocessAudioUrl(null);
			});
		return () => {
			cancelled = true;
		};
	}, [reprocessCandidate?.artifacts?.finalAudioGcsUri]);

	const openPiiReprocess = (call: Call, e?: React.MouseEvent) => {
		e?.stopPropagation();
		setReprocessCall(call);
		setReprocessCandidateId(null);
		setReprocessCandidate(null);
		setReprocessAudioUrl(null);
	};

	const handleStartPiiReprocess = async () => {
		if (!reprocessCall) return;
		setIsStartingReprocess(true);
		try {
			const response = await startCallPiiReprocess({
				companyId: reprocessCall.companyId,
				callSid: reprocessCall.id,
			});
			setReprocessCandidateId(response.data.candidateId);
			toast.success("Long/Chirp reprocessing started");
		} catch (error: unknown) {
			console.error("Error starting PII reprocessing:", error);
			toast.error(getErrorMessage(error, "Failed to start PII reprocessing"));
		} finally {
			setIsStartingReprocess(false);
		}
	};

	const handleResolvePiiReprocess = async (action: "promote" | "discard") => {
		if (!reprocessCall || !reprocessCandidateId) return;
		setIsResolvingReprocess(true);
		try {
			await resolveCallPiiReprocess({
				companyId: reprocessCall.companyId,
				callSid: reprocessCall.id,
				candidateId: reprocessCandidateId,
				action,
			});
			toast.success(
				action === "promote"
					? "Candidate transcript and audio promoted"
					: "Candidate discarded",
			);
			if (action === "discard") {
				setReprocessCall(null);
				setReprocessCandidateId(null);
				setReprocessCandidate(null);
				setReprocessAudioUrl(null);
			}
		} catch (error: unknown) {
			console.error("Error resolving PII candidate:", error);
			toast.error(getErrorMessage(error, "Failed to resolve PII candidate"));
		} finally {
			setIsResolvingReprocess(false);
		}
	};

	const handlePurgeTwilioRecording = async (
		call: Call,
		e?: React.MouseEvent,
	) => {
		if (e) e.stopPropagation();
		setPurgingTwilioCallIds((current) => new Set(current).add(call.id));
		try {
			const res = await purgeCallTwilioRecording({
				companyId: call.companyId,
				callSid: call.id,
			});
			const data = res.data;
			toast.success(
				data.purgedRecordingSids?.length
					? `Purged ${data.purgedRecordingSids.length} recording(s) from Twilio`
					: "Twilio recording marked as purged",
			);
		} catch (error: unknown) {
			console.error("Error purging Twilio recording:", error);
			toast.error(getErrorMessage(error, "Failed to purge Twilio recording"));
		} finally {
			setPurgingTwilioCallIds((current) => {
				const next = new Set(current);
				next.delete(call.id);
				return next;
			});
		}
	};

	const handlePurgePiiSidecar = async (call: Call, e?: React.MouseEvent) => {
		if (e) e.stopPropagation();
		setPurgingPiiSidecarCallIds((current) => new Set(current).add(call.id));
		try {
			const res = await purgeCallPiiSidecar({
				companyId: call.companyId,
				callSid: call.id,
			});
			const data = res.data;
			toast.success(
				data.deletedSidecarPaths?.length
					? `Purged PII metadata sidecar from GCP (${data.deletedSidecarPaths.length} file(s))`
					: "PII metadata sidecar marked as purged",
			);
		} catch (error: unknown) {
			console.error("Error purging PII sidecar:", error);
			toast.error(getErrorMessage(error, "Failed to purge PII sidecar"));
		} finally {
			setPurgingPiiSidecarCallIds((current) => {
				const next = new Set(current);
				next.delete(call.id);
				return next;
			});
		}
	};

	const handleViewPiiSidecar = async (call: Call) => {
		setIsLoadingPiiSidecar(true);
		try {
			const result = await getCallPiiSidecar({
				companyId: call.companyId,
				callSid: call.id,
			});
			setPiiSidecar({
				path: result.data.sidecarPath,
				data: result.data.sidecar,
			});
		} catch (error: unknown) {
			console.error("Error loading PII sidecar:", error);
			toast.error(getErrorMessage(error, "Failed to load PII sidecar"));
		} finally {
			setIsLoadingPiiSidecar(false);
		}
	};

	const handleDeleteCall = async () => {
		if (!callPendingDeletion) return;
		setIsDeletingCall(true);
		try {
			await deleteCallCascade({
				companyId: callPendingDeletion.companyId,
				callSid: callPendingDeletion.id,
			});
			toast.success(`Call ${callPendingDeletion.id} was deleted.`);
			setCallPendingDeletion(null);
			setIsDetailsDialogOpen(false);
			setSelectedCall(null);
			if (onSelectedCallSidChange) {
				onSelectedCallSidChange(null);
			} else {
				window.history.pushState(null, "", "/calls");
			}
		} catch (error: unknown) {
			console.error("Error deleting call:", error);
			toast.error(getErrorMessage(error, "Failed to delete call"));
		} finally {
			setIsDeletingCall(false);
		}
	};

	const navigateToCall = useCallback(
		(callId: string) => {
			const callIndex = filteredCalls.findIndex((call) => call.id === callId);
			if (callIndex === -1) return;
			setCurrentPage(Math.floor(callIndex / itemsPerPage) + 1);
			setSelectedCall(filteredCalls[callIndex]);
			if (onSelectedCallSidChange) {
				onSelectedCallSidChange(callId);
			} else {
				window.history.pushState(
					null,
					"",
					`/calls/${encodeURIComponent(callId)}`,
				);
			}
		},
		[filteredCalls, onSelectedCallSidChange],
	);
	const navigateToPreviousCall = useCallback(() => {
		if (previousCallId) navigateToCall(previousCallId);
	}, [navigateToCall, previousCallId]);
	const navigateToNextCall = useCallback(() => {
		if (nextCallId) navigateToCall(nextCallId);
	}, [navigateToCall, nextCallId]);

	useCallNavigationShortcuts({
		enabled:
			isDetailsDialogOpen &&
			!audioUrl &&
			!piiSidecar &&
			!reviewCall &&
			!reprocessCall &&
			!callPendingDeletion,
		onPrevious: previousCallId ? navigateToPreviousCall : undefined,
		onNext: nextCallId ? navigateToNextCall : undefined,
	});

	return (
		<div className="space-y-6">
			<div>
				<h1 className="text-3xl font-bold tracking-tight">Calls</h1>
				<p className="text-slate-600">View calls from all companies</p>
			</div>

			{/* Audio Player Dialog */}
			<Dialog
				open={!!audioUrl}
				onOpenChange={(open) => {
					if (!open) {
						setAudioUrl(null);
						setPlayingCallId(null);
					}
				}}
			>
				<DialogContent className="sm:max-w-md">
					<DialogHeader>
						<DialogTitle>Play Recording</DialogTitle>
					</DialogHeader>
					<div className="space-y-4">
						<div className="flex items-center justify-center p-6 bg-gray-50 rounded-lg">
							{audioUrl && (
								<audio
									ref={audioRef}
									controls
									autoPlay
									className="w-full"
									src={audioUrl}
									onRateChange={(e) =>
										setPlaybackRate(e.currentTarget.playbackRate)
									}
								>
									<track kind="captions" />
									Your browser does not support the audio element.
								</audio>
							)}
						</div>
						<div className="flex justify-center gap-2">
							{[1, 1.5, 2].map((rate) => (
								<Button
									key={rate}
									variant={playbackRate === rate ? "default" : "outline"}
									size="sm"
									onClick={() => {
										if (audioRef.current) {
											audioRef.current.playbackRate = rate;
										}
									}}
								>
									{rate}x
								</Button>
							))}
						</div>
					</div>
				</DialogContent>
			</Dialog>

			<Dialog
				open={piiSidecar !== null}
				onOpenChange={(open) => {
					if (!open) setPiiSidecar(null);
				}}
			>
				<DialogContent className="max-h-[85vh] max-w-4xl overflow-hidden">
					<DialogHeader>
						<DialogTitle>PII Sidecar (system-admin debugging)</DialogTitle>
						<DialogDescription className="break-all font-mono text-xs">
							{piiSidecar?.path}
						</DialogDescription>
					</DialogHeader>
					<ScrollArea className="max-h-[65vh] rounded-md border bg-slate-950 p-4">
						<pre className="whitespace-pre-wrap break-words text-xs text-slate-100">
							{piiSidecar ? JSON.stringify(piiSidecar.data, null, 2) : ""}
						</pre>
					</ScrollArea>
				</DialogContent>
			</Dialog>

			<Dialog
				open={correctionCall !== null}
				onOpenChange={(open) => {
					if (!open && !openingCorrection) setCorrectionCall(null);
				}}
			>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Open PII correction</DialogTitle>
						<DialogDescription>
							Customer access to this call is paused while the correction is
							reviewed. Enter the reason for reopening it.
						</DialogDescription>
					</DialogHeader>
					<Textarea
						aria-label="Correction reason"
						value={correctionReason}
						onChange={(event) => setCorrectionReason(event.target.value)}
						maxLength={1000}
					/>
					<DialogFooter>
						<Button
							variant="outline"
							onClick={() => setCorrectionCall(null)}
							disabled={openingCorrection}
						>
							Cancel
						</Button>
						<Button
							onClick={() => void handleOpenCorrection()}
							disabled={openingCorrection || !correctionReason.trim()}
						>
							Open correction
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
			<Dialog
				open={reviewCall !== null}
				onOpenChange={(open) => {
					if (!open && !isApprovingPiiReview) {
						setReviewCall(null);
						setReviewReason("");
					}
				}}
			>
				<DialogContent className="sm:max-w-lg">
					<DialogHeader>
						<DialogTitle>Approve PII review</DialogTitle>
						<DialogDescription>
							This accepts the available redaction matches exactly as reviewed.
							It does not retry detection or change the recorded alignment
							coverage.
						</DialogDescription>
					</DialogHeader>
					{reviewCall?.piiProcessing?.alignment && (
						<div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-950">
							<span className="font-semibold">Recorded coverage: </span>
							{reviewCall.piiProcessing.alignment.resolvedOccurrences}/
							{reviewCall.piiProcessing.alignment.expectedOccurrences}{" "}
							occurrence checks resolved.
						</div>
					)}
					<div className="space-y-2">
						<label htmlFor="pii-review-reason" className="text-sm font-medium">
							Review note
						</label>
						<Textarea
							id="pii-review-reason"
							value={reviewReason}
							onChange={(event) => setReviewReason(event.target.value)}
							placeholder="What did you verify, and why are the remaining occurrences acceptable?"
							maxLength={1000}
							disabled={isApprovingPiiReview}
						/>
					</div>
					<DialogFooter>
						<Button
							variant="outline"
							onClick={() => setReviewCall(null)}
							disabled={isApprovingPiiReview}
						>
							Cancel
						</Button>
						<Button
							onClick={() => void handleApprovePiiReview()}
							disabled={isApprovingPiiReview || !reviewReason.trim()}
							className="gap-1.5 bg-emerald-700 text-white hover:bg-emerald-800"
						>
							{isApprovingPiiReview && (
								<Loader2 className="h-4 w-4 animate-spin" />
							)}
							Approve as safe
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			<Dialog
				open={reprocessCall !== null}
				onOpenChange={(open) => {
					if (!open && !isStartingReprocess && !isResolvingReprocess) {
						setReprocessCall(null);
						setReprocessCandidateId(null);
						setReprocessCandidate(null);
						setReprocessAudioUrl(null);
					}
				}}
			>
				<DialogContent className="max-h-[92vh] max-w-6xl overflow-y-auto">
					<DialogHeader>
						<DialogTitle>Reprocess PII from original recording</DialogTitle>
						<DialogDescription>
							Long and Chirp will create an isolated transcript, audio, and PII
							candidate. The live call is unchanged until you approve
							replacement.
						</DialogDescription>
					</DialogHeader>
					{!reprocessCandidateId ? (
						<div className="space-y-4">
							<div className="rounded-md border border-slate-200 bg-slate-50 p-4 text-sm">
								The original Twilio recording must still be available. Candidate
								artifacts are deleted if you discard them.
							</div>
							<DialogFooter>
								<Button
									variant="outline"
									onClick={() => setReprocessCall(null)}
								>
									Cancel
								</Button>
								<Button
									onClick={() => void handleStartPiiReprocess()}
									disabled={isStartingReprocess}
									className="gap-1.5"
								>
									{isStartingReprocess && (
										<Loader2 className="h-4 w-4 animate-spin" />
									)}
									Start isolated reprocessing
								</Button>
							</DialogFooter>
						</div>
					) : !reprocessCandidate ? (
						<div className="flex items-center justify-center gap-2 p-10 text-sm text-slate-600">
							<Loader2 className="h-4 w-4 animate-spin" /> Loading candidate…
						</div>
					) : (
						<div className="space-y-5">
							<div className="flex items-center justify-between rounded-md border p-3 text-sm">
								<span>
									Candidate status: <strong>{reprocessCandidate.status}</strong>
								</span>
								{["queued", "acquiring", "processing"].includes(
									reprocessCandidate.status,
								) && <Loader2 className="h-4 w-4 animate-spin" />}
							</div>
							{reprocessCandidate.failureReason && (
								<div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
									{reprocessCandidate.failureReason}
								</div>
							)}
							{reprocessCandidate.preview && (
								<>
									<div className="grid gap-4 md:grid-cols-2">
										<div className="space-y-2">
											<h3 className="text-sm font-semibold">
												Current transcript
											</h3>
											<ScrollArea className="h-80 rounded-md border bg-slate-50 p-4">
												<pre className="whitespace-pre-wrap break-words font-sans text-xs">
													{reprocessCandidate.original?.formattedTranscript ||
														"No current formatted transcript"}
												</pre>
											</ScrollArea>
										</div>
										<div className="space-y-2">
											<h3 className="text-sm font-semibold">
												Candidate transcript
											</h3>
											<ScrollArea className="h-80 rounded-md border bg-emerald-50/40 p-4">
												<pre className="whitespace-pre-wrap break-words font-sans text-xs">
													{reprocessCandidate.preview.formattedTranscript}
												</pre>
											</ScrollArea>
										</div>
									</div>
									<div className="space-y-2 rounded-md border p-4">
										<h3 className="text-sm font-semibold">Candidate audio</h3>
										{reprocessAudioUrl ? (
											<audio
												controls
												className="w-full"
												src={reprocessAudioUrl}
											>
												<track kind="captions" />
												Your browser does not support the audio element.
											</audio>
										) : (
											<p className="text-xs text-slate-500">
												Candidate audio is not available yet.
											</p>
										)}
									</div>
									<div className="grid gap-3 rounded-md border p-4 text-sm md:grid-cols-3">
										<div>
											<span className="font-semibold">Detected entities: </span>
											{reprocessCandidate.preview.entities?.length ?? 0}
										</div>
										<div>
											<span className="font-semibold">Alignment: </span>
											{reprocessCandidate.preview.alignment
												?.resolvedOccurrences ?? 0}
											/
											{reprocessCandidate.preview.alignment
												?.expectedOccurrences ?? 0}
										</div>
										<div className="truncate">
											<span className="font-semibold">Types: </span>
											{[
												...new Set(
													reprocessCandidate.preview.entities?.map(
														(entity: { type: string }) => entity.type,
													) ?? [],
												),
											].join(", ") || "None"}
										</div>
									</div>
								</>
							)}
							<DialogFooter>
								<Button
									variant="outline"
									onClick={() => void handleResolvePiiReprocess("discard")}
									disabled={
										isResolvingReprocess ||
										!["ready", "review_required", "failed"].includes(
											reprocessCandidate.status,
										)
									}
								>
									Discard candidate
								</Button>
								<Button
									onClick={() => void handleResolvePiiReprocess("promote")}
									disabled={
										isResolvingReprocess ||
										!reprocessAudioUrl ||
										!["ready", "cleanup_failed"].includes(
											reprocessCandidate.status,
										)
									}
									className="gap-1.5 bg-emerald-700 hover:bg-emerald-800"
								>
									{isResolvingReprocess && (
										<Loader2 className="h-4 w-4 animate-spin" />
									)}
									Approve replacement
								</Button>
							</DialogFooter>
						</div>
					)}
				</DialogContent>
			</Dialog>

			<AlertDialog
				open={callPendingDeletion !== null}
				onOpenChange={(open) => {
					if (!open && !isDeletingCall) setCallPendingDeletion(null);
				}}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Permanently delete this call?</AlertDialogTitle>
						<AlertDialogDescription>
							This removes call {callPendingDeletion?.id} and every document
							nested beneath it. This action cannot be undone and will be
							recorded in the audit log.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel disabled={isDeletingCall}>
							Cancel
						</AlertDialogCancel>
						<AlertDialogAction
							onClick={(event) => {
								event.preventDefault();
								void handleDeleteCall();
							}}
							disabled={isDeletingCall}
							className="bg-red-600 text-white hover:bg-red-700"
						>
							{isDeletingCall ? "Deleting..." : "Delete call"}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			{/* Call Details Dialog */}
			<Dialog
				open={isDetailsDialogOpen}
				onOpenChange={(open) => {
					setIsDetailsDialogOpen(open);
					if (!open) {
						setSelectedCall(null);
						if (onSelectedCallSidChange) {
							onSelectedCallSidChange(null);
						} else {
							window.history.pushState(null, "", "/calls");
						}
					}
				}}
			>
				<DialogContent className="w-[96vw] max-w-none h-[96vh] flex flex-col p-0 overflow-hidden shadow-2xl border-none gap-0">
					<DialogHeader className="px-8 py-6 border-b bg-white/95 backdrop-blur-md z-10 shrink-0 shadow-sm flex-col gap-2">
						<div className="flex items-center justify-between">
							<DialogTitle className="flex items-center gap-3 text-2xl font-bold text-slate-900">
								<div className="p-2 bg-blue-50 rounded-lg">
									<Phone className="w-6 h-6 text-blue-600" />
								</div>
								Call Details
							</DialogTitle>
							{activeCall && (
								<div className="flex items-center gap-3">
									<div className="hidden items-center gap-2 sm:flex">
										<Button
											variant="outline"
											size="sm"
											onClick={navigateToPreviousCall}
											disabled={!previousCallId}
											aria-label="Previous call"
										>
											<ChevronLeft className="h-4 w-4" />
											Previous
											<kbd className="rounded border bg-slate-50 px-1.5 py-0.5 font-mono text-[10px]">
												K
											</kbd>
										</Button>
										<Button
											variant="outline"
											size="sm"
											onClick={navigateToNextCall}
											disabled={!nextCallId}
											aria-label="Next call"
										>
											Next
											<kbd className="rounded border bg-slate-50 px-1.5 py-0.5 font-mono text-[10px]">
												J
											</kbd>
											<ChevronRight className="h-4 w-4" />
										</Button>
									</div>
									{activePiiPresentation && (
										<PiiRedactionBadge presentation={activePiiPresentation} />
									)}
									<Button
										variant="destructive"
										size="sm"
										onClick={() => setCallPendingDeletion(activeCall)}
										className="gap-1.5"
									>
										<Trash2 className="h-3.5 w-3.5" />
										Delete Call
									</Button>
								</div>
							)}
						</div>
						<DialogDescription className="font-mono text-xs mt-2 bg-slate-100 w-fit px-2 py-0.5 rounded text-slate-500">
							ID: {activeCall?.id}
						</DialogDescription>
					</DialogHeader>

					<div className="flex-1 overflow-y-auto bg-slate-50/30 scroll-smooth">
						<div className="px-8 py-10 max-w-7xl mx-auto w-full">
							{activeCall && (
								<div className="space-y-10 pb-10">
									{/* Header Info */}
									<div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
										<div className="space-y-2">
											<h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider">
												Caller Information
											</h4>
											<div className="bg-slate-50 p-3 rounded-lg border border-slate-100">
												<div className="font-semibold text-lg text-slate-900">
													{activeCall.caller.name}
												</div>
												<div className="text-sm text-slate-600 flex items-center gap-1.5 mt-1">
													<Phone className="w-3.5 h-3.5" />
													{activeCall.caller.phone}
												</div>
												{activeCall.caller.email && (
													<div className="text-sm text-slate-600 flex items-center gap-1.5 mt-1">
														<Info className="w-3.5 h-3.5" />
														{activeCall.caller.email}
													</div>
												)}
											</div>
										</div>
										<div className="space-y-2">
											<h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider">
												Call Metadata
											</h4>
											<div className="grid grid-cols-2 gap-y-2 gap-x-4 text-sm bg-slate-50 p-3 rounded-lg border border-slate-100">
												<span className="text-slate-500">Occurred At:</span>
												<span className="font-medium">
													{activeCall.occurredAt.toLocaleString()}
												</span>

												<span className="text-slate-500">Created At:</span>
												<span className="font-medium">
													{new Date(activeCall.createdAt).toLocaleString()}
												</span>

												<span className="text-slate-500">Latency:</span>
												<span className="font-medium">
													{activeCall.latencyMs !== undefined
														? (activeCall.latencyMs / 1000).toFixed(1) + "s"
														: "-"}
												</span>

												<span className="text-slate-500">Duration:</span>
												<span className="font-mono font-medium">
													{activeCall.duration}
												</span>

												<span className="text-slate-500">Agent:</span>
												<span className="font-medium">{activeCall.agent}</span>

												<span className="text-slate-500">Company:</span>
												<span className="font-medium">
													{getCompanyName(activeCall.companyId)}
												</span>

												<span className="text-slate-500">Status:</span>
												<div className="flex items-center gap-2">
													<Badge
														variant={
															activeCall.status === CALL_STATUS.COMPLETED
																? "secondary"
																: "default"
														}
														className="w-fit h-5 text-[10px] uppercase font-bold px-1.5"
													>
														{activeCall.status}
													</Badge>
													{activeCall.transcriptionWarning?.code ===
														"single-transcript-warning" && (
														<Badge
															variant="outline"
															className="border-amber-700 bg-amber-50 text-amber-900"
															title={`Only one transcript was available. Failed recognizer: ${activeCall.transcriptionWarning.failedRecognizerIds.join(", ")}`}
														>
															Single transcript warning
														</Badge>
													)}
													{activeCall.isTestCall && (
														<Badge
															variant="outline"
															className="bg-[#FBEAF0] text-[#72243E] border-[#ED93B1] rounded-[20px] text-[9px] h-4 px-1.5 flex items-center gap-1 font-medium shrink-0 whitespace-nowrap"
														>
															<FlaskConical className="w-2.5 h-2.5" />
															Test Number
														</Badge>
													)}
												</div>
											</div>
										</div>
									</div>

									{/* Privacy & Retention Lifecycle Panel */}
									<div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm space-y-4">
										<div className="flex items-center justify-between border-b pb-3">
											<div className="flex items-center gap-2">
												<ShieldCheck className="w-5 h-5 text-indigo-600" />
												<h4 className="text-sm font-bold text-slate-900">
													Privacy & Data Retention Lifecycle
												</h4>
											</div>
											<span className="text-xs text-slate-500">
												Redaction state and privacy actions
											</span>
										</div>

										<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
											{/* Step 1: PII Redaction */}
											<div className="flex flex-col justify-between p-4 rounded-lg border border-slate-100 bg-slate-50/50 space-y-3">
												<div>
													<span className="text-xs font-bold text-slate-500 uppercase tracking-wider">
														PII Redaction
													</span>
													<p className="text-xs text-slate-600 mt-2 leading-relaxed">
														{activePiiPresentation?.description}
													</p>
													<PiiAlignmentMethodBadges
														alignment={activeCall.piiProcessing?.alignment}
													/>
												</div>
												<div className="space-y-2">
													<Button
														variant="outline"
														size="sm"
														className="w-full text-xs"
														onClick={() =>
															onOpenPiiCase?.(
																activeCall.companyId,
																activeCall.id,
															)
														}
													>
														Open in PII review
													</Button>
													{(activeCall.isPiiRedacted ||
														activeCall.piiProcessing?.detection ===
															"disabled") && (
														<Button
															variant="outline"
															size="sm"
															className="w-full text-xs"
															onClick={() => {
																setCorrectionCall(activeCall);
																setCorrectionReason("");
															}}
														>
															Open correction
														</Button>
													)}
													{activePiiPresentation?.canApproveReview && (
														<Button
															variant="default"
															size="sm"
															onClick={() => openPiiReview(activeCall)}
															className="w-full gap-1.5 bg-emerald-700 text-xs font-medium hover:bg-emerald-800"
														>
															<ShieldCheck className="h-3 w-3" />
															Approve PII Review
														</Button>
													)}
													<Button
														variant="outline"
														size="sm"
														onClick={() => handleRedactPii(activeCall)}
														disabled={
															redactingCallIds.has(activeCall.id) ||
															!activePiiPresentation?.canRunRedaction
														}
														className="w-full text-xs font-medium gap-1.5"
													>
														{redactingCallIds.has(activeCall.id) ? (
															<>
																<Loader2 className="w-3 h-3 animate-spin" />
																Redacting...
															</>
														) : (
															<>
																{activePiiPresentation?.kind === "complete" ? (
																	<ShieldCheck className="w-3 h-3 text-emerald-600" />
																) : activePiiPresentation?.kind === "partial" ||
																	activePiiPresentation?.kind ===
																		"processing" ? (
																	<Loader2
																		className={`w-3 h-3 text-amber-600 ${
																			activePiiPresentation?.kind ===
																			"processing"
																				? "animate-spin"
																				: ""
																		}`}
																	/>
																) : (
																	<ShieldAlert
																		className={`w-3 h-3 ${
																			activePiiPresentation?.kind === "error"
																				? "text-red-600"
																				: "text-slate-500"
																		}`}
																	/>
																)}
																{activePiiPresentation?.kind === "complete"
																	? "Redaction Complete"
																	: activePiiPresentation?.actionLabel}
															</>
														)}
													</Button>
													<Button
														variant="outline"
														size="sm"
														onClick={() => openPiiReprocess(activeCall)}
														disabled={
															activeCall.isTwilioRecordingPurged ||
															activePiiPresentation?.kind === "processing"
														}
														className="w-full gap-1.5 text-xs font-medium"
													>
														<Sparkles className="h-3 w-3 text-indigo-600" />
														Reprocess from Original
													</Button>
												</div>
											</div>

											{/* Step 3: Twilio Recording */}
											<div className="flex flex-col justify-between p-4 rounded-lg border border-slate-100 bg-slate-50/50 space-y-3 md:order-3">
												<div>
													<div className="flex items-center justify-between">
														<span className="text-xs font-bold text-slate-500 uppercase tracking-wider">
															3. Twilio Cloud Recording
														</span>
														{activeCall.isTwilioRecordingPurged ? (
															<Badge
																variant="outline"
																className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px] font-semibold gap-1"
															>
																<ShieldCheck className="w-3 h-3" />
																Purged
															</Badge>
														) : (
															<Badge
																variant="outline"
																className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]"
															>
																Retained on Twilio
															</Badge>
														)}
													</div>
													<p className="text-xs text-slate-500 mt-1">
														Permanently deletes the raw recording from Twilio
														servers.
													</p>
												</div>
												<Button
													variant="outline"
													size="sm"
													onClick={() => handlePurgeTwilioRecording(activeCall)}
													disabled={
														purgingTwilioCallIds.has(activeCall.id) ||
														activeCall.isTwilioRecordingPurged ||
														!activeCall.gcsUri
													}
													className="w-full text-xs font-medium gap-1.5"
												>
													{purgingTwilioCallIds.has(activeCall.id) ? (
														<>
															<Loader2 className="w-3 h-3 animate-spin" />
															Purging...
														</>
													) : activeCall.isTwilioRecordingPurged ? (
														<>
															<ShieldCheck className="w-3 h-3 text-emerald-600" />
															Purged from Twilio
														</>
													) : (
														<>
															<Trash2 className="w-3 h-3 text-red-600" />
															Purge Twilio Audio
														</>
													)}
												</Button>
											</div>

											{/* Step 2: PII Sidecar JSON */}
											<div className="flex flex-col justify-between p-4 rounded-lg border border-slate-100 bg-slate-50/50 space-y-3 md:order-2">
												<div>
													<div className="flex items-center justify-between">
														<span className="text-xs font-bold text-slate-500 uppercase tracking-wider">
															2. GCP PII Sidecar Metadata
														</span>
														{activeCall.isPiiSidecarPurged ? (
															<Badge
																variant="outline"
																className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px] font-semibold gap-1"
															>
																<ShieldCheck className="w-3 h-3" />
																Purged
															</Badge>
														) : (
															<Badge
																variant="outline"
																className="bg-slate-100 text-slate-600 border-slate-200 text-[10px]"
															>
																{activePiiSidecarPresentation?.badgeLabel ||
																	"Stored in GCP"}
															</Badge>
														)}
													</div>
													<p className="text-xs text-slate-500 mt-1">
														{activePiiSidecarPresentation?.description ||
															"Deletes the companion JSON detection file from Cloud Storage."}
													</p>
												</div>
												<div className="space-y-2">
													{activePiiSidecarPresentation?.canView && (
														<Button
															variant="outline"
															size="sm"
															onClick={() => handleViewPiiSidecar(activeCall)}
															disabled={
																isLoadingPiiSidecar || !activeCall.gcsUri
															}
															className="w-full gap-1.5 text-xs font-medium"
														>
															{isLoadingPiiSidecar ? (
																<Loader2 className="h-3 w-3 animate-spin" />
															) : (
																<Eye className="h-3 w-3" />
															)}
															View PII Sidecar
														</Button>
													)}
													<Button
														variant="outline"
														size="sm"
														onClick={() => handlePurgePiiSidecar(activeCall)}
														disabled={
															purgingPiiSidecarCallIds.has(activeCall.id) ||
															!activePiiSidecarPresentation?.canPurge ||
															activePiiPresentation?.kind !== "complete" ||
															!activeCall.isPiiRedacted
														}
														className="w-full text-xs font-medium gap-1.5"
													>
														{purgingPiiSidecarCallIds.has(activeCall.id) ? (
															<>
																<Loader2 className="w-3 h-3 animate-spin" />
																Purging...
															</>
														) : activeCall.isPiiSidecarPurged ? (
															<>
																<ShieldCheck className="w-3 h-3 text-emerald-600" />
																Purged from GCP
															</>
														) : activePiiSidecarPresentation?.status ===
															"not_created" ? (
															<>
																<ShieldCheck className="w-3 h-3 text-slate-400" />
																No Sidecar to Purge
															</>
														) : (
															<>
																<Trash2 className="w-3 h-3 text-red-600" />
																Purge PII Sidecar
															</>
														)}
													</Button>
												</div>
											</div>
										</div>
									</div>

									{activeOutboundContext?.isOutbound && (
										<div className="rounded-xl border border-cyan-100 bg-cyan-50/60 p-5">
											<div className="mb-4 flex flex-wrap items-center gap-2">
												<Badge className="bg-cyan-700 text-white">
													Outbound
												</Badge>
												{activeOutboundContext.playbookName && (
													<span className="font-semibold text-cyan-950">
														{activeOutboundContext.playbookName}
													</span>
												)}
												{activeOutboundContext.outcome && (
													<Badge variant="outline" className="bg-white">
														Outcome: {activeOutboundContext.outcome}
													</Badge>
												)}
												{activeOutboundContext.taskStatus && (
													<Badge variant="outline" className="bg-white">
														Task: {activeOutboundContext.taskStatus}
													</Badge>
												)}
											</div>
											<div className="grid gap-4 text-sm md:grid-cols-3">
												<div>
													<div className="text-xs font-bold uppercase tracking-wider text-cyan-900/70">
														Goal
													</div>
													<div className="mt-1 text-cyan-950">
														{activeOutboundContext.playbook?.description ||
															activeOutboundContext.playbookName ||
															"Outbound playbook call"}
													</div>
												</div>
												<div>
													<div className="text-xs font-bold uppercase tracking-wider text-cyan-900/70">
														Attempt
													</div>
													<div className="mt-1 text-cyan-950">
														{activeOutboundContext.attemptNumber
															? `Attempt ${activeOutboundContext.attemptNumber}`
															: "Attempt details unavailable"}
														{activeOutboundContext.maxAttempts
															? ` of ${activeOutboundContext.maxAttempts}`
															: ""}
													</div>
												</div>
												<div>
													<div className="text-xs font-bold uppercase tracking-wider text-cyan-900/70">
														Run
													</div>
													<div className="mt-1 truncate font-mono text-xs text-cyan-950">
														{activeOutboundContext.runId || "Unavailable"}
													</div>
												</div>
											</div>
											{activeOutboundContext.summary && (
												<div className="mt-4 text-sm text-cyan-950">
													<span className="font-semibold">Task summary: </span>
													{activeOutboundContext.summary}
												</div>
											)}
											{activeOutboundInputEntries.length > 0 && (
												<div className="mt-4">
													<div className="mb-2 text-xs font-bold uppercase tracking-wider text-cyan-900/70">
														Task input
													</div>
													<div className="grid gap-2 md:grid-cols-3">
														{activeOutboundInputEntries.map((entry) => (
															<div
																key={entry.key}
																className="rounded-md border border-cyan-100 bg-white px-3 py-2"
															>
																<div className="text-[11px] uppercase text-slate-500">
																	{entry.label}
																</div>
																<div className="truncate text-sm text-slate-900">
																	{entry.value}
																</div>
															</div>
														))}
													</div>
												</div>
											)}
											{activeOutboundResultEntries.length > 0 && (
												<div className="mt-4">
													<div className="mb-2 text-xs font-bold uppercase tracking-wider text-cyan-900/70">
														Result
													</div>
													<div className="grid gap-2 md:grid-cols-3">
														{activeOutboundResultEntries.map((entry) => (
															<div
																key={entry.key}
																className="rounded-md border border-cyan-100 bg-white px-3 py-2"
															>
																<div className="text-[11px] uppercase text-slate-500">
																	{entry.label}
																</div>
																<div className="truncate text-sm text-slate-900">
																	{entry.value}
																</div>
															</div>
														))}
													</div>
												</div>
											)}
										</div>
									)}

									<Separator className="opacity-50" />

									{/* Evaluation Feedback */}
									{activeCall.evaluationStatus === "completed" && (
										<div
											className={`p-4 rounded-xl border ${activeCall.isProblematic ? "bg-red-50/50 border-red-100" : "bg-amber-50/10 border-amber-100/50"}`}
										>
											<div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
												<div>
													<h4 className="text-sm font-bold flex items-center gap-2">
														<Sparkles
															className={`w-4 h-4 ${activeCall.isProblematic ? "text-red-500" : "text-amber-500"}`}
														/>
														AI Quality Evaluation
													</h4>
													<p className="mt-1 text-xs text-slate-600">
														Evaluation type:{" "}
														{activeCall.evaluationRubric ===
														"inbound_receptionist"
															? "Inbound receptionist"
															: activeCall.evaluationRubric ===
																	"outbound_playbook"
																? "Outbound playbook"
																: "Not recorded"}
													</p>
												</div>
												<div className="flex flex-wrap items-center gap-2">
													{activeCall.evaluationScore && (
														<div className="flex items-center gap-3 bg-white/80 backdrop-blur-sm px-3 py-1.5 rounded-full border border-slate-200/50 shadow-sm">
															<span className="text-[10px] font-bold text-slate-400 uppercase tracking-tight">
																Overall Score
															</span>
															<div className="flex items-center gap-2">
																<div className="h-1.5 w-16 bg-slate-100 rounded-full overflow-hidden">
																	<div
																		className={`h-full rounded-full ${
																			activeCall.evaluationScore > 80
																				? "bg-green-500"
																				: activeCall.evaluationScore > 50
																					? "bg-amber-500"
																					: "bg-red-500"
																		}`}
																		style={{
																			width: `${activeCall.evaluationScore}%`,
																		}}
																	/>
																</div>
																<span
																	className={`text-sm font-black ${
																		activeCall.evaluationScore > 80
																			? "text-green-600"
																			: activeCall.evaluationScore > 50
																				? "text-amber-600"
																				: "text-red-600"
																	}`}
																>
																	{activeCall.evaluationScore}%
																</span>
															</div>
														</div>
													)}
													<Button
														variant="outline"
														size="sm"
														className="h-8 gap-2 rounded-full bg-white/80"
														onClick={() =>
															handleRunEvaluation(activeCall, true)
														}
														disabled={isEvaluating}
													>
														{isEvaluating ? (
															<Loader2 className="w-3.5 h-3.5 animate-spin" />
														) : (
															<Sparkles className="w-3.5 h-3.5" />
														)}
														Re-run
													</Button>
												</div>
											</div>

											{isLoadingEvaluation ? (
												<div className="flex flex-col items-center justify-center py-10 gap-3 bg-white/30 rounded-lg">
													<Loader2 className="w-6 h-6 animate-spin text-slate-400" />
													<span className="text-xs text-slate-500 font-medium animate-pulse">
														Analyzing evaluation details...
													</span>
												</div>
											) : evaluationReport ? (
												<div className="space-y-4">
													{evaluationReport.isProblematic && (
														<div className="p-3 bg-red-100/40 border border-red-200/50 rounded-lg flex items-start gap-3 shadow-sm">
															<AlertCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
															<div>
																<div className="text-[10px] font-black text-red-700 uppercase mb-0.5 tracking-tight">
																	Flagged for Review
																</div>
																<div className="text-sm text-red-900 leading-snug font-semibold">
																	{evaluationReport.problematicReason}
																</div>
															</div>
														</div>
													)}

													<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
														{/* Agent Performance */}
														<Card className="shadow-sm border-slate-200/60 bg-white/60 backdrop-blur-sm overflow-hidden">
															<div className="bg-slate-50/50 border-b border-slate-100 px-3 py-2">
																<h5 className="text-[10px] font-black uppercase tracking-widest text-slate-400 flex items-center gap-1.5">
																	<User className="w-3 h-3" />
																	Agent Interaction
																</h5>
															</div>
															<CardContent className="p-3 space-y-3 pt-4">
																<div className="space-y-1.5">
																	<div className="flex justify-between text-[11px] font-bold text-slate-600">
																		<span>Handling & Tone</span>
																		<span
																			className={
																				evaluationReport.agentPerformance
																					.handlingScore > 70
																					? "text-green-600"
																					: evaluationReport.agentPerformance
																								.handlingScore > 40
																						? "text-amber-600"
																						: "text-red-600"
																			}
																		>
																			{
																				evaluationReport.agentPerformance
																					.handlingScore
																			}
																			/100
																		</span>
																	</div>
																	<div className="h-1 w-full bg-slate-100 rounded-full overflow-hidden">
																		<div
																			className={`h-full rounded-full ${
																				evaluationReport.agentPerformance
																					.handlingScore > 70
																					? "bg-green-500"
																					: evaluationReport.agentPerformance
																								.handlingScore > 40
																						? "bg-amber-500"
																						: "bg-red-500"
																			}`}
																			style={{
																				width: `${evaluationReport.agentPerformance.handlingScore}%`,
																			}}
																		/>
																	</div>
																</div>
																<div className="space-y-1.5">
																	<div className="flex justify-between text-[11px] font-bold text-slate-600">
																		<span>Directive Adherence</span>
																		<span
																			className={
																				evaluationReport.agentPerformance
																					.adherenceToDirectivesScore > 70
																					? "text-green-600"
																					: evaluationReport.agentPerformance
																								.adherenceToDirectivesScore > 40
																						? "text-amber-600"
																						: "text-red-600"
																			}
																		>
																			{
																				evaluationReport.agentPerformance
																					.adherenceToDirectivesScore
																			}
																			/100
																		</span>
																	</div>
																	<div className="h-1 w-full bg-slate-100 rounded-full overflow-hidden">
																		<div
																			className={`h-full rounded-full ${
																				evaluationReport.agentPerformance
																					.adherenceToDirectivesScore > 70
																					? "bg-green-500"
																					: evaluationReport.agentPerformance
																								.adherenceToDirectivesScore > 40
																						? "bg-amber-500"
																						: "bg-red-500"
																			}`}
																			style={{
																				width: `${evaluationReport.agentPerformance.adherenceToDirectivesScore}%`,
																			}}
																		/>
																	</div>
																</div>
																<div className="mt-2 text-xs text-slate-700 leading-relaxed italic bg-blue-50/30 p-2.5 rounded-lg border border-blue-100/30 relative">
																	<span className="absolute -top-2 left-2 px-1 bg-white text-[9px] font-bold text-blue-400 uppercase border border-blue-50 rounded">
																		Feedback
																	</span>
																	{evaluationReport.agentPerformance.feedback}
																</div>
															</CardContent>
														</Card>

														{/* AI Processing Quality */}
														<Card className="shadow-sm border-slate-200/60 bg-white/60 backdrop-blur-sm overflow-hidden">
															<div className="bg-slate-50/50 border-b border-slate-100 px-3 py-2">
																<h5 className="text-[10px] font-black uppercase tracking-widest text-slate-400 flex items-center gap-1.5">
																	<CheckSquare className="w-3 h-3" />
																	Data Accuracy
																</h5>
															</div>
															<CardContent className="p-3 space-y-3 pt-4">
																<div className="grid grid-cols-3 gap-2">
																	<div className="text-center p-2 rounded-lg bg-slate-50/50 border border-slate-100">
																		<div className="text-[9px] text-slate-400 uppercase font-black tracking-tighter">
																			Summary
																		</div>
																		<div
																			className={`text-sm font-black ${evaluationReport.postProcessingQuality.summaryScore >= 8 ? "text-green-600" : "text-amber-600"}`}
																		>
																			{
																				evaluationReport.postProcessingQuality
																					.summaryScore
																			}
																			<span className="text-[10px] text-slate-300">
																				/10
																			</span>
																		</div>
																	</div>
																	<div className="text-center p-2 rounded-lg bg-slate-50/50 border border-slate-100">
																		<div className="text-[9px] text-slate-400 uppercase font-black tracking-tighter">
																			Tags
																		</div>
																		<div
																			className={`text-sm font-black ${evaluationReport.postProcessingQuality.tagsScore >= 8 ? "text-green-600" : "text-amber-600"}`}
																		>
																			{
																				evaluationReport.postProcessingQuality
																					.tagsScore
																			}
																			<span className="text-[10px] text-slate-300">
																				/10
																			</span>
																		</div>
																	</div>
																	<div className="text-center p-2 rounded-lg bg-slate-50/50 border border-slate-100">
																		<div className="text-[9px] text-slate-400 uppercase font-black tracking-tighter">
																			Naming
																		</div>
																		<div
																			className={`text-sm font-black ${evaluationReport.postProcessingQuality.nameScore >= 8 ? "text-green-600" : "text-amber-600"}`}
																		>
																			{
																				evaluationReport.postProcessingQuality
																					.nameScore
																			}
																			<span className="text-[10px] text-slate-300">
																				/10
																			</span>
																		</div>
																	</div>
																</div>
																<div className="mt-2 text-xs text-slate-700 leading-relaxed italic bg-purple-50/30 p-2.5 rounded-lg border border-purple-100/30 relative">
																	<span className="absolute -top-2 left-2 px-1 bg-white text-[9px] font-bold text-purple-400 uppercase border border-purple-50 rounded">
																		Feedback
																	</span>
																	{
																		evaluationReport.postProcessingQuality
																			.feedback
																	}
																</div>
															</CardContent>
														</Card>
													</div>

													<div className="flex items-center justify-between text-[10px] text-slate-400 font-medium px-1">
														<div className="flex items-center gap-4">
															<span>
																Input Tokens:{" "}
																{activeCall.evaluationUsage?.inputTokens ||
																	"N/A"}
															</span>
															<span>
																Output Tokens:{" "}
																{activeCall.evaluationUsage?.outputTokens ||
																	"N/A"}
															</span>
														</div>
														<div className="flex items-center gap-1">
															<Info className="w-3 h-3" />
															Estimated cost: $
															{activeCall.evaluationCost?.toFixed(4) ||
																"0.0000"}
														</div>
													</div>
												</div>
											) : (
												<div className="text-sm text-slate-500 italic text-center py-6 bg-white/50 rounded-lg border border-dashed border-slate-200">
													Detailed report not found. The record might have been
													archived or is still being processed.
												</div>
											)}
										</div>
									)}

									{activeCall.evaluationStatus !== "completed" &&
										activeCall.evaluationStatus !== "pending" && (
											<div className="flex flex-col items-center justify-center p-8 bg-slate-50 rounded-xl border-2 border-dashed border-slate-200 gap-3">
												<div className="p-3 bg-white rounded-full shadow-sm border border-slate-100">
													<Sparkles className="w-6 h-6 text-amber-500" />
												</div>
												<div className="text-center space-y-1">
													<div className="font-bold text-slate-900">
														No Evaluation Found
													</div>
													<p className="text-xs text-slate-500 max-w-[240px]">
														Would you like to run an AI quality analysis on this
														call now?
													</p>
												</div>
												<Button
													variant="default"
													className="mt-2 gap-2 bg-primary hover:bg-primary/90 px-6"
													onClick={() => handleRunEvaluation(activeCall)}
													disabled={isEvaluating}
												>
													{isEvaluating ? (
														<>
															<Loader2 className="w-4 h-4 animate-spin" />
															Running Analysis...
														</>
													) : (
														<>
															<Sparkles className="w-4 h-4" />
															Run AI Quality Evaluation
														</>
													)}
												</Button>
											</div>
										)}

									{/* Title & Summary */}
									{(activeCall.title || activeCallFallback) && (
										<div className="space-y-2">
											<h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider flex items-center gap-2">
												<FileText className="w-3.5 h-3.5" />
												Call Title
											</h4>
											<p
												className={`font-bold bg-white p-4 rounded-xl border border-slate-200 shadow-sm leading-tight text-lg ${activeCallFallback ? "text-slate-400 italic" : "text-slate-900"}`}
											>
												{activeCallFallback
													? activeCallFallback.title
													: activeCall.title}
											</p>
										</div>
									)}

									{(activeCall.summary || activeCallFallback) && (
										<div className="space-y-2">
											<h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider">
												Executive Summary
											</h4>
											<div
												className={`text-sm leading-relaxed bg-white p-4 rounded-xl border border-slate-200 shadow-sm ${activeCallFallback ? "text-slate-400 italic" : "text-slate-700"}`}
											>
												{activeCallFallback
													? activeCallFallback.summary
													: activeCall.summary}
											</div>
										</div>
									)}

									{/* Action Items */}
									{activeCall.actionItems &&
										activeCall.actionItems.length > 0 && (
											<div className="space-y-3">
												<h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider flex items-center gap-2">
													<CheckSquare className="w-3.5 h-3.5" />
													Required Actions
												</h4>
												<div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
													{activeCall.actionItems.map((item, idx) => (
														<div
															key={item.id || idx}
															className="bg-white border border-slate-200 p-4 rounded-xl shadow-sm hover:shadow-md transition-shadow"
														>
															<div className="flex justify-between items-center mb-3">
																<Badge
																	variant={
																		item.status === "closed"
																			? "secondary"
																			: "default"
																	}
																	className="text-[9px] h-4.5 font-black uppercase"
																>
																	{item.status}
																</Badge>
																<span className="text-[10px] font-bold text-slate-300 uppercase tracking-tighter">
																	{item.type}
																</span>
															</div>
															{item.action.type === "followUp" && (
																<div className="space-y-1">
																	<div className="font-bold text-slate-900">
																		Follow Up: {item.action.customerName}
																	</div>
																	<div className="text-xs text-slate-600 line-clamp-3">
																		{item.action.details}
																	</div>
																</div>
															)}
															{item.action.type === "booking" && (
																<div className="space-y-1">
																	<div className="font-bold text-slate-900">
																		Booking: {item.action.customerName}
																	</div>
																	<div className="text-xs text-slate-600">
																		{item.action.bookingDate} •{" "}
																		{item.action.duration}m •{" "}
																		{item.action.accommodationType}
																	</div>
																</div>
															)}
														</div>
													))}
												</div>
											</div>
										)}

									<Separator className="opacity-50" />

									{/* Transcript */}
									{activeCall.transcript && (
										<div className="space-y-2">
											<h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider flex items-center gap-2">
												<FileText className="w-3.5 h-3.5" />
												Call Transcript
											</h4>
											{activeCall.collectedNumbers &&
												activeCall.collectedNumbers.length > 0 && (
													<div className="flex flex-wrap gap-2">
														{activeCall.collectedNumbers.map((item, index) => (
															<span
																key={`${item.kind}-${index}`}
																className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs"
															>
																<span className="font-semibold">
																	{item.label}:{" "}
																</span>
																<span className="font-mono">{item.value}</span>
															</span>
														))}
													</div>
												)}
											<ScrollArea className="h-64 rounded-xl border border-slate-200 bg-slate-50/50">
												<div className="p-4 text-xs font-mono text-slate-700 whitespace-pre-wrap leading-relaxed">
													{activeCall.transcript}
												</div>
											</ScrollArea>
										</div>
									)}

									{/* Audio Control Sticky Placeholder / Spacing */}
									<div className="h-24" />
								</div>
							)}
						</div>
					</div>

					{/* Sticky Footer for Audio */}
					{activeCall?.gcsUri && (
						<div className="shrink-0 p-6 bg-white/95 backdrop-blur-md border-t border-slate-200 flex justify-center shadow-[0_-10px_20px_rgba(0,0,0,0.04)] rounded-b-xl z-20">
							<Button
								onClick={(e) => handlePlayAudio(activeCall, e)}
								className={`gap-4 px-10 rounded-full h-14 transition-colors text-base ${playingCallId === activeCall.id ? "bg-amber-500 hover:bg-amber-600" : "bg-primary hover:bg-primary/90"}`}
							>
								{playingCallId === activeCall.id ? (
									<Loader2 className="w-6 h-6 animate-spin" />
								) : (
									<Play className="w-6 h-6 fill-current" />
								)}
								<span className="font-black uppercase tracking-widest text-xs">
									{playingCallId === activeCall.id
										? "Loading Stream..."
										: "Play Call Recording"}
								</span>
							</Button>
						</div>
					)}
				</DialogContent>
			</Dialog>

			<Card>
				<CardHeader>
					<CardTitle>Recent Calls</CardTitle>
					<CardDescription>
						Displaying {startIndex + 1}-
						{Math.min(endIndex, filteredCalls.length)} of {filteredCalls.length}{" "}
						calls
					</CardDescription>
				</CardHeader>
				<CardContent className="space-y-4">
					{/* Filters */}
					<div className="flex flex-wrap items-center gap-4">
						<div className="flex-1 min-w-[220px] relative">
							<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
							<Input
								placeholder="Search callers, agents, titles, or Call SID..."
								value={searchQuery}
								onChange={(e) => setSearchQuery(e.target.value)}
								className="pl-10"
							/>
						</div>
						<Select
							value={selectedCompanyId}
							onValueChange={setSelectedCompanyId}
						>
							<SelectTrigger className="w-[200px]">
								<SelectValue placeholder="Filter by Company" />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="all">All Companies</SelectItem>
								{companies.map((company) => (
									<SelectItem key={company.id} value={company.id}>
										{company.name}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
						<Select
							value={directionFilter}
							onValueChange={(value) =>
								setDirectionFilter(value as "all" | "inbound" | "outbound")
							}
						>
							<SelectTrigger className="w-[150px]">
								<SelectValue placeholder="Direction" />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="all">All directions</SelectItem>
								<SelectItem value="inbound">Inbound</SelectItem>
								<SelectItem value="outbound">Outbound</SelectItem>
							</SelectContent>
						</Select>
						<Select value={playbookFilter} onValueChange={setPlaybookFilter}>
							<SelectTrigger className="w-[220px]">
								<SelectValue placeholder="Playbook" />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="all">All playbooks</SelectItem>
								{playbookFilterOptions.map((playbook) => (
									<SelectItem key={playbook.id} value={playbook.id}>
										{playbook.name}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
						<div className="flex items-center gap-2 whitespace-nowrap">
							<Switch
								id="hide-empty-calls"
								checked={hideEmptyCalls}
								onCheckedChange={setHideEmptyCalls}
							/>
							<label htmlFor="hide-empty-calls" className="text-sm font-medium">
								Hide empty calls
							</label>
						</div>
					</div>

					<p className="text-xs text-muted-foreground">
						Scroll the call table horizontally to inspect all fields and
						actions.
					</p>
					{/* Table */}
					<Table className="min-w-[1600px]">
						<TableHeader>
							<TableRow>
								<TableHead>Date & Time</TableHead>
								<TableHead>Call SID</TableHead>
								<TableHead>Caller</TableHead>
								<TableHead></TableHead>
								<TableHead>Callee</TableHead>
								<TableHead>Title</TableHead>
								<TableHead>Company</TableHead>
								<TableHead>Status</TableHead>
								<TableHead>PII Redaction</TableHead>
								<TableHead>Eval</TableHead>
								<TableHead>Action Items</TableHead>
								<TableHead>Duration</TableHead>
								<TableHead>Latency</TableHead>
								<TableHead></TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{paginatedCalls.length === 0 ? (
								<TableRow>
									<TableCell
										colSpan={14}
										className="text-center py-8 text-slate-500"
									>
										No calls found.
									</TableCell>
								</TableRow>
							) : (
								paginatedCalls.map((call) => {
									const outboundContext = outboundContextByCallId.get(call.id);
									const piiPresentation = getPiiRedactionPresentation(call);
									const participants = resolveCallParticipantDisplay(
										call,
										outboundContext,
									);
									return (
										<TableRow
											key={call.id}
											className="cursor-pointer hover:bg-slate-50"
											onClick={() => handleViewDetails(call)}
										>
											<TableCell>
												<div className="text-sm font-medium">{call.date}</div>
												<div className="text-xs text-slate-500">
													{call.time}
												</div>
											</TableCell>
											<TableCell>
												<div
													className="text-xs font-mono text-slate-500 max-w-[100px] truncate"
													title={call.id}
												>
													{call.id}
												</div>
											</TableCell>
											<TableCell>
												<div className="flex items-center gap-2">
													<div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 shrink-0">
														<User className="w-3 h-3" />
													</div>
													<div className="min-w-0">
														<div className="text-sm font-medium truncate">
															{participants.caller.name}
														</div>
														<div className="text-xs text-slate-500 truncate">
															{participants.caller.phone}
														</div>
													</div>
												</div>
											</TableCell>
											<TableCell className="w-8 px-0 text-center">
												<ArrowRight className="mx-auto h-4 w-4 text-slate-400" />
											</TableCell>
											<TableCell>
												<div className="min-w-0">
													<div className="text-sm font-medium truncate">
														{participants.callee.name}
													</div>
													<div className="text-xs text-slate-500 truncate">
														{participants.callee.phone || "—"}
													</div>
												</div>
											</TableCell>
											<TableCell>
												{(() => {
													const fallback = getCallFallbackState(call);
													return (
														<div className="max-w-[170px] space-y-1">
															{outboundContext?.playbookName && (
																<div className="flex items-center gap-1.5">
																	<Badge className="h-4 bg-cyan-700 px-1.5 text-[9px] text-white">
																		{outboundContext.playbookName}
																	</Badge>
																</div>
															)}
															<div
																className={`text-sm truncate flex items-center gap-1.5 ${fallback ? "text-slate-400 italic" : ""}`}
																title={fallback ? fallback.title : call.title}
															>
																{fallback?.kind === "processing" && (
																	<Loader2 className="w-3.5 h-3.5 animate-spin" />
																)}
																{fallback?.kind === "failed" && (
																	<AlertCircle className="w-3.5 h-3.5" />
																)}
																{fallback?.kind === "no-audio" && (
																	<MicOff className="w-3.5 h-3.5" />
																)}
																{fallback?.title || call.title || (
																	<span className="text-slate-400 italic">
																		No Title
																	</span>
																)}
															</div>
															{outboundContext?.outcome && (
																<div className="truncate text-xs text-cyan-700">
																	Outcome: {outboundContext.outcome}
																</div>
															)}
														</div>
													);
												})()}
											</TableCell>
											<TableCell>
												<Badge
													variant="outline"
													className="text-xs font-normal max-w-[120px] truncate"
												>
													{getCompanyName(call.companyId)}
												</Badge>
											</TableCell>
											<TableCell>
												<div className="flex flex-col gap-1 items-start">
													<Badge
														variant={
															call.status === CALL_STATUS.COMPLETED
																? "secondary"
																: "default"
														}
														className="text-[10px]"
													>
														{call.status}
													</Badge>
													{call.transcriptionWarning?.code ===
														"single-transcript-warning" && (
														<Badge
															variant="outline"
															className="border-amber-700 bg-amber-50 text-amber-900 text-[10px]"
															title="Only one transcript was available after transcription retries"
														>
															Single transcript warning
														</Badge>
													)}
													{call.isTestCall && (
														<Badge
															variant="outline"
															className="bg-[#FBEAF0] text-[#72243E] border-[#ED93B1] rounded-[20px] text-[9px] h-4 px-1.5 flex items-center gap-1 font-medium shrink-0 whitespace-nowrap"
														>
															<FlaskConical className="w-2.5 h-2.5" />
															Test Number
														</Badge>
													)}
												</div>
											</TableCell>
											<TableCell>
												<div className="flex flex-col items-start gap-1.5">
													<PiiRedactionBadge
														presentation={piiPresentation}
														compact
													/>
													<PiiAlignmentMethodBadges
														alignment={call.piiProcessing?.alignment}
														compact
													/>
													<div className="flex flex-col items-stretch gap-1">
														{piiPresentation.canApproveReview && (
															<Button
																variant="outline"
																size="sm"
																onClick={(e) => openPiiReview(call, e)}
																className="h-6 justify-start px-1.5 text-[10px] text-emerald-700"
															>
																<ShieldCheck className="mr-1 h-3 w-3" />
																Approve review
															</Button>
														)}
														<Button
															variant="outline"
															size="sm"
															onClick={(e) => handleRedactPii(call, e)}
															className="h-6 justify-start px-1.5 text-[10px]"
															disabled={
																redactingCallIds.has(call.id) ||
																!piiPresentation.canRunRedaction
															}
															title={piiPresentation.actionLabel}
														>
															{redactingCallIds.has(call.id) && (
																<Loader2 className="mr-1 h-3 w-3 animate-spin" />
															)}
															Redact PII
														</Button>
														<Button
															variant="outline"
															size="sm"
															onClick={(e) => openPiiReprocess(call, e)}
															className="h-6 justify-start px-1.5 text-[10px]"
															disabled={
																call.isTwilioRecordingPurged ||
																piiPresentation.kind === "processing"
															}
														>
															<Sparkles className="mr-1 h-3 w-3" />
															Reprocess
														</Button>
														<Button
															variant="outline"
															size="sm"
															onClick={(e) =>
																handlePurgeTwilioRecording(call, e)
															}
															className="h-6 justify-start px-1.5 text-[10px] text-red-700"
															disabled={
																call.isTwilioRecordingPurged ||
																!call.gcsUri ||
																purgingTwilioCallIds.has(call.id)
															}
															title="Purge Twilio Recording"
														>
															{purgingTwilioCallIds.has(call.id) && (
																<Loader2 className="mr-1 h-3 w-3 animate-spin" />
															)}
															Purge Twilio
														</Button>
													</div>
												</div>
											</TableCell>
											<TableCell>
												{call.evaluationStatus === "completed" ? (
													<div className="flex flex-col gap-1">
														<Badge
															className={
																call.evaluationScore &&
																call.evaluationScore > 80
																	? "bg-green-100 text-green-700"
																	: call.evaluationScore &&
																			call.evaluationScore > 50
																		? "bg-amber-100 text-amber-700"
																		: "bg-red-100 text-red-700"
															}
														>
															{call.evaluationScore}/100
														</Badge>
														{call.isProblematic && (
															<Badge
																variant="destructive"
																className="text-[8px] h-4 py-0 flex items-center gap-0.5"
															>
																<ShieldAlert className="w-2 h-2" />
																Issue
															</Badge>
														)}
													</div>
												) : call.evaluationStatus === "pending" ? (
													<span className="text-[10px] text-slate-400 animate-pulse">
														Pending...
													</span>
												) : (
													<span className="text-slate-300">-</span>
												)}
											</TableCell>
											<TableCell>
												{call.actionItems && call.actionItems.length > 0 ? (
													<Badge
														variant="default"
														className="bg-primary hover:bg-primary/90"
													>
														{call.actionItems.length} Items
													</Badge>
												) : (
													<span className="text-slate-400 text-xs">-</span>
												)}
											</TableCell>
											<TableCell className="font-mono text-sm">
												{call.duration}
											</TableCell>
											<TableCell className="font-mono text-sm">
												{call.latencyMs !== undefined
													? (call.latencyMs / 1000).toFixed(1) + "s"
													: "-"}
											</TableCell>
											<TableCell>
												<div className="flex items-center justify-end gap-1">
													{call.gcsUri && (
														<Button
															variant="ghost"
															size="sm"
															onClick={(e) => handlePlayAudio(call, e)}
															className="h-8 w-8 p-0"
															disabled={playingCallId === call.id}
															title="Play Audio"
														>
															{playingCallId === call.id ? (
																<Loader2 className="w-4 h-4 animate-spin" />
															) : (
																<Play className="w-4 h-4" />
															)}
														</Button>
													)}
												</div>
											</TableCell>
										</TableRow>
									);
								})
							)}
						</TableBody>
					</Table>

					{/* Pagination */}
					{totalPages > 1 && (
						<div className="mt-6 flex justify-center">
							<Pagination>
								<PaginationContent>
									<PaginationItem>
										<PaginationPrevious
											onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
											className={
												currentPage === 1
													? "pointer-events-none opacity-50"
													: "cursor-pointer"
											}
											size={undefined}
										/>
									</PaginationItem>

									{Array.from({ length: totalPages }, (_, i) => i + 1)
										.filter((page) => {
											if (page === 1 || page === totalPages) return true;
											if (Math.abs(page - currentPage) <= 1) return true;
											return false;
										})
										.map((page, index, array) => (
											<Fragment key={page}>
												{index > 0 && array[index - 1] !== page - 1 && (
													<PaginationItem key={`ellipsis-${page}`}>
														<PaginationEllipsis />
													</PaginationItem>
												)}
												<PaginationItem>
													<PaginationLink
														onClick={() => setCurrentPage(page)}
														isActive={currentPage === page}
														className="cursor-pointer"
														size={undefined}
													>
														{page}
													</PaginationLink>
												</PaginationItem>
											</Fragment>
										))}

									<PaginationItem>
										<PaginationNext
											onClick={() =>
												setCurrentPage((p) => Math.min(totalPages, p + 1))
											}
											className={
												currentPage === totalPages
													? "pointer-events-none opacity-50"
													: "cursor-pointer"
											}
											size={undefined}
										/>
									</PaginationItem>
								</PaginationContent>
							</Pagination>
						</div>
					)}
				</CardContent>
			</Card>
		</div>
	);
}
